<template>
    <v-container>
        <v-row class="fill-height" align="center" justify="center">
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title class="headline">Update Password</v-card-title>
                    <v-card-text>
                        <v-form ref="form" v-model="valid" lazy-validation>
                            <v-text-field
                                v-model="password"
                                :rules="[rules.required, rules.min]"
                                label="New Password"
                                type="password"
                                required
                            ></v-text-field>
                            <v-text-field
                                v-model="confirmPassword"
                                :rules="[rules.required, rules.match]"
                                label="Confirm Password"
                                type="password"
                                required
                            ></v-text-field>
                            <v-text-field
                                v-if="showMfaInput"
                                v-model="mfaCode"
                                label="Authenticator Code (MFA)"
                                type="text"
                                outlined
                                :rules="[v => !!v || 'Code is required']"
                            ></v-text-field>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn color="primary" @click="updatePassword" :disabled="!sessionReady">Update Password</v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-dialog v-model="dialog" max-width="500px">
                <v-card>
                    <v-card-title class="headline">Password Updated!</v-card-title>
                    <v-card-text>
                        <p>Your password has been successfully updated, please log in with your new password.</p>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn color="primary" @click="close">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-snackbar
            v-model="snackbar"
            :color="snackbarColor"
            timeout="5000"
            top
        >
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    v-bind="attrs"
                    @click="snackbar = false"
                >
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import {supabase} from '../supabase'
export default {
    data() {
        return {
            password: '',
            confirmPassword: '',
            mfaCode: '',
            showMfaInput: false,
            valid: false,
            dialog: false,
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'error',
            sessionReady: false,
            rules: {
                required: (v) => !!v || 'Required.',
                min: (v) => (v && v.length >= 6) || 'Min 6 characters',
                match: (v) => v === this.password || 'Passwords must match',
            },
        };
    },
    async mounted() {
        // Check for error in URL
        const hash = window.location.hash
        if (hash && (hash.includes('error=') || hash.includes('error_code='))) {
             this.snackbarText = 'Invalid or expired password reset link.'
             this.snackbarColor = 'error'
             this.snackbar = true
             return
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
            this.sessionReady = true;
            this.checkMfaRequirement();
        }

        this.authListener = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') {
                this.sessionReady = true;
                this.checkMfaRequirement();
                console.log(session);
            }
        });
    },
    beforeDestroy() {
        if (this.authListener && this.authListener.data && this.authListener.data.subscription) {
            this.authListener.data.subscription.unsubscribe();
        }
    },
    methods: {
        async checkMfaRequirement() {
            const { data: { user } } = await supabase.auth.getUser(); // Get user to check factors
            const hasVerifiedMfa = user && user.factors && user.factors.some(f => f.status === 'verified');
            
            const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            const isAal2 = aal && aal.currentLevel === 'aal2';

            // Show MFA input only if MFA is enabled AND the current session is not AAL2
            this.showMfaInput = hasVerifiedMfa && !isAal2;
        },
        async updatePassword() {
            if (!this.sessionReady) {
                 this.snackbarText = 'Session not active. Please request a new password reset link.';
                 this.snackbarColor = 'error';
                 this.snackbar = true;
                 return;
            }
            if (this.$refs.form.validate()) {
                
                // Handle MFA if required
                if (this.showMfaInput) {
                    if (!this.mfaCode) {
                        this.snackbarText = 'Please enter your Authenticator Code';
                        this.snackbarColor = 'warning';
                        this.snackbar = true;
                        return;
                    }

                    try {
                        const { data: { user } } = await supabase.auth.getUser();
                        const factor = user.factors.find(f => f.status === 'verified');
                        if (!factor) throw new Error('No verified MFA factor found.');

                        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
                        if (challengeError) throw challengeError;

                        const { error: verifyError } = await supabase.auth.mfa.verify({
                            factorId: factor.id,
                            challengeId: challengeData.id,
                            code: this.mfaCode
                        });

                        
                        if (verifyError) throw verifyError;
                        
                        // If successful, session is now AAL2, proceed to update
                    } catch (e) {
                        console.error('MFA Verification failed:', e);
                        this.snackbarText = 'MFA Verification failed: ' + e.message;
                        this.snackbarColor = 'error';
                        this.snackbar = true;
                        return;
                    }
                }

                const { error } = await supabase.auth.updateUser({
                    password: this.password
                })
                
                if (error) {
                    console.error('Error updating password:', error);
                    this.snackbarText = error.message;
                    // If error is specifically AAL2 required (and we missed it somehow), show input
                    if (error.message.includes('AAL2') || error.message.includes('security level')) {
                        this.showMfaInput = true;
                        this.snackbarText = 'Two-factor authentication required. Please enter code.';
                    }
                    this.snackbarColor = 'error';
                    this.snackbar = true;
                } else {
                    this.dialog = true;
                }
            }
        },

        close() {
            this.dialog = false;
            this.password = '';
            this.confirmPassword = '';
            this.mfaCode = '';
            supabase.auth.signOut()
            this.$store.commit('SET_UP', false)
            this.$router.push('/')
        },
    },
};
</script>

<style scoped>
</style>